import { getLiquidTemplateVariableRegex } from "./sms";

interface TransformFunctions<T> {
  onText: (text: string) => T;
  onLink: (url: string) => T;
  onDiscount: (discountCode: string) => T;
  onDiscountLink: (discountId: string) => T;
  onVariable: (variable: string, defaultValue?: string) => T;
  onNewline: () => T;
}

/**
 * Generic function to parse SMS text with markdown links, discount codes, variables and newlines
 * to use this async, just have T be a Promise.
 * @param content The SMS text content to parse
 * @param transforms Object containing transform functions for each type of content
 * @returns Array of transformed segments
 */
export function parseAndTransformSmsText<T>(
  content: string,
  transforms: TransformFunctions<T>,
): T[] {
  const segments: T[] = [];
  let lastIndex = 0;
  const variableRegex = getLiquidTemplateVariableRegex();
  const discountVariableRegex = /(%%(?<discountCode>[^%]+)%%)/;
  // Discount link pattern: $$discountId$$ - generates checkout URL with discount auto-applied
  const discountLinkVariableRegex = /(\$\$(?<discountLinkId>[^$]+)\$\$)/;
  const linkRegex = /(\[\]\((?<url>[^)]+)\))/;
  // URL regex to detect plain URLs in the text (only with protocol)
  const plainUrlRegex =
    /(?<plainUrl>\b(?:(?:https?|ftp):\/\/)[^\s<>"']+[^\s<>"'.;,!?)]*)/i;
  const newlineRegex = /(?<newline>\n)/;
  // Combined regex to match all patterns
  const combinedRegex = new RegExp(
    `${linkRegex.source}|${plainUrlRegex.source}|${discountLinkVariableRegex.source}|${discountVariableRegex.source}|${variableRegex.source}|${newlineRegex.source}`,
    "g",
  );

  let match;

  while ((match = combinedRegex.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push(transforms.onText(content.slice(lastIndex, match.index)));
    }

    const { groups } = match;

    if (groups?.newline) {
      segments.push(transforms.onNewline());
    } else if (groups?.url) {
      //This is a markdown link
      segments.push(transforms.onLink(groups.url));
    } else if (groups?.plainUrl) {
      // Handle plain URLs detected in the text
      segments.push(transforms.onLink(groups.plainUrl));
    } else if (groups?.discountLinkId) {
      // Handle discount link pattern $$discountId$$
      segments.push(transforms.onDiscountLink(groups.discountLinkId));
    } else if (groups?.discountCode) {
      segments.push(transforms.onDiscount(groups.discountCode));
    } else if (groups?.variable) {
      segments.push(transforms.onVariable(groups.variable.trim()));
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    segments.push(transforms.onText(content.slice(lastIndex)));
  }

  return segments;
}

/**
 * Extracts all discount IDs from a template string.
 * Discount IDs are in the format %%discountId%% (code) or $$discountId$$ (link)
 */
export function extractDiscountIdsFromTemplate(content: string): string[] {
  const discountIds: string[] = [];
  parseAndTransformSmsText(content, {
    onText: () => {},
    onLink: () => {},
    onDiscount: (discountId) => {
      discountIds.push(discountId);
    },
    onDiscountLink: (discountId) => {
      discountIds.push(discountId);
    },
    onVariable: () => {},
    onNewline: () => {},
  });
  return discountIds;
}

/**
 * Checks if template content consists only of a discount code variable.
 * Returns the discount ID if the template is only %%discountId%% or $$discountId$$ (with optional whitespace),
 * otherwise returns undefined.
 *
 * This is used to detect when ShopperAI should be triggered with discount capability
 * instead of using the template directly.
 */
export function extractDiscountIdIfOnlyDiscount(
  content: string,
): string | undefined {
  const trimmed = content.trim();
  // Match %%discountId%% or $$discountId$$ patterns
  const discountCodeMatch = trimmed.match(/^%%([^%]+)%%$/);
  const discountLinkMatch = trimmed.match(/^\$\$([^$]+)\$\$$/);

  if (discountCodeMatch) {
    return discountCodeMatch[1];
  }
  if (discountLinkMatch) {
    return discountLinkMatch[1];
  }
  return undefined;
}
